import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiMail, FiPlus, FiTrash2 } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import LoadingButton from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { formatDateOnly, localTodayIsoDate } from '../utils/formatTimestamp';
import formatMoney from '../utils/formatMoney';
import {
  TAX_ID_TYPE_OPTIONS,
  TAX_REGIME_OPTIONS,
  GST_TREATMENT_OPTIONS,
  defaultTaxIdTypeForCountry,
  defaultRegimeForCountry,
  selectOrgTaxProfile,
  taxIdLabel,
} from '../utils/taxDocumentFields';
import { appAlert, appConfirm } from '../utils/appDialog';
import MoreMenu from '../components/MoreMenu';
import { renderMarkdownToHtml } from '../utils/boardMarkdown';
import './Services.css';
import '../components/erp/Erp.css';

const money = formatMoney;

function emptyLine() {
  return { description: '', quantity: 1, unit_price: 0, sac_hsn: '' };
}

function emptyDocForm(docType = 'quotation') {
  return {
    doc_type: docType,
    status: 'draft',
    company_id: '',
    customer_id: '',
    template_id: '',
    client_name: '',
    client_email: '',
    client_address: '',
    client_tax_id: '',
    client_tax_id_type: '',
    place_of_supply: '',
    tax_regime: 'manual',
    gst_treatment: 'regular',
    tax_exempt: false,
    currency: 'USD',
    tax_rate: 0,
    requisition_id: '',
    notes: '',
    terms: '',
    valid_until: '',
    issued_at: localTodayIsoDate(),
    due_at: '',
    lines: [emptyLine(), emptyLine()],
  };
}

function formFromDetail(detail) {
  return {
    doc_type: detail.doc_type,
    status: detail.status,
    company_id: detail.company_id ? String(detail.company_id) : '',
    customer_id: detail.customer_id ? String(detail.customer_id) : '',
    template_id: detail.template_id ? String(detail.template_id) : '',
    client_name: detail.client_name || '',
    client_email: detail.client_email || '',
    client_address: detail.client_address || '',
    client_tax_id: detail.client_tax_id || '',
    client_tax_id_type: detail.client_tax_id_type || '',
    place_of_supply: detail.place_of_supply || '',
    tax_regime: detail.tax_regime || 'manual',
    gst_treatment: detail.gst_treatment || 'regular',
    tax_exempt: Boolean(detail.tax_exempt),
    currency: detail.currency || 'USD',
    tax_rate: detail.tax_rate || 0,
    requisition_id: detail.requisition_id ? String(detail.requisition_id) : '',
    notes: detail.notes || '',
    terms: detail.terms || '',
    valid_until: detail.valid_until || '',
    issued_at: detail.issued_at || '',
    due_at: detail.due_at || '',
    lines:
      detail.lines?.length > 0
        ? detail.lines.map((l) => ({
            description: l.description,
            quantity: l.quantity,
            unit_price: l.unit_price,
            sac_hsn: l.sac_hsn || '',
          }))
        : [emptyLine()],
  };
}

/**
 * Full-page create/edit for customer quotations and invoices (was modal on Sales).
 */
const SalesDocumentDetails = () => {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('service_sales_manage');
  const isNew = !documentId || documentId === 'new';
  const preferredType = searchParams.get('type') === 'invoice' ? 'invoice' : 'quotation';
  const presetRequisitionId = searchParams.get('requisition_id') || '';
  const readOnly = !canManage;

  const [docForm, setDocForm] = useState(() => emptyDocForm(preferredType));
  const [ready, setReady] = useState(isNew);
  const hydratedNewRef = useRef(false);

  usePageTitle(
    isNew
      ? preferredType === 'invoice'
        ? 'New invoice'
        : 'New quotation'
      : docForm.client_name || 'Sales document'
  );

  const { data: companies = [] } = useQuery(
    queryKeys.serviceCompanies,
    () => api.get('/services/companies').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );

  const { data: templates = [] } = useQuery(
    ['service-sales-templates'],
    () => api.get('/services/sales/templates').then((r) => r.data),
    { staleTime: 60_000 }
  );

  const { data: customers = [] } = useQuery(
    queryKeys.serviceCustomers,
    () => api.get('/services/customers').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );

  const { data: requisitions = [] } = useQuery(
    ['requisitions', 'quotation-link'],
    () =>
      api.get('/requisitions', { params: { limit: 200 } }).then((r) =>
        Array.isArray(r.data) ? r.data : r.data?.requisitions || []
      ),
    { enabled: docForm.doc_type === 'quotation', staleTime: 30_000, retry: false }
  );

  const { data: linkedRequisition } = useQuery(
    ['requisition', docForm.requisition_id],
    () => api.get(`/requisitions/${docForm.requisition_id}`).then((r) => r.data),
    {
      enabled: docForm.doc_type === 'quotation' && Boolean(docForm.requisition_id),
      staleTime: 15_000,
      retry: false,
    }
  );

  const { data: currencies = [] } = useQuery(
    'currencies-active',
    () => api.get('/currencies').then((r) => r.data),
    { staleTime: 60_000 }
  );

  const { data: orgTax } = useQuery(
    ['org-tax-profile'],
    () => api.get('/services/finance/org-tax-profile').then((r) => r.data),
    { staleTime: 0, retry: false }
  );

  const {
    data: detail,
    isLoading: loadingDetail,
    isError,
    error,
  } = useQuery(
    ['sales-document', documentId],
    () => api.get(`/services/sales/documents/${documentId}`).then((r) => r.data),
    { enabled: !isNew && Boolean(documentId), staleTime: 0 }
  );

  useEffect(() => {
    if (isNew) {
      // Seed once so companies/templates refresh does not overwrite user currency edits.
      if (hydratedNewRef.current) {
        setDocForm((prev) => {
          if (prev.company_id) return prev;
          if (!companies[0]?.id) return prev;
          const brandCurrency = companies[0].erp_base_currency;
          return {
            ...prev,
            company_id: String(companies[0].id),
            ...(brandCurrency && !prev.requisition_id ? { currency: brandCurrency } : {}),
          };
        });
        return;
      }
      setDocForm((prev) => {
        const next = emptyDocForm(preferredType);
        if (companies[0]?.id) {
          next.company_id = String(companies[0].id);
          if (companies[0].erp_base_currency) {
            next.currency = companies[0].erp_base_currency;
          }
        }
        const preferred = (templates || []).find(
          (t) => t.is_default && (t.doc_type === preferredType || t.doc_type === 'both')
        );
        if (preferred) next.template_id = String(preferred.id);
        if (preferredType === 'quotation' && presetRequisitionId) {
          next.requisition_id = String(presetRequisitionId);
        }
        const brandCurrency = companies[0]?.erp_base_currency;
        return {
          ...next,
          currency: brandCurrency || prev.currency || next.currency,
        };
      });
      hydratedNewRef.current = true;
      setReady(true);
      return;
    }
    hydratedNewRef.current = false;
    if (detail) {
      setDocForm(formFromDetail(detail));
      setReady(true);
    }
  }, [isNew, preferredType, presetRequisitionId, detail, companies, templates]);

  const applyServiceBrand = (companyId) => {
    const brand = (companies || []).find((c) => String(c.id) === String(companyId));
    const base = brand?.erp_base_currency;
    setDocForm((prev) => ({
      ...prev,
      company_id: companyId ? String(companyId) : '',
      ...(base ? { currency: base } : {}),
    }));
  };

  const applyCustomerToForm = async (customerId) => {
    setDocForm((prev) => ({ ...prev, customer_id: customerId ? String(customerId) : '' }));
    if (!customerId) return;
    try {
      const customer = await api.get(`/services/customers/${customerId}`).then((r) => r.data);
      const primary =
        (customer.stakeholders || []).find((s) => s.is_primary) ||
        (customer.stakeholders || [])[0];
      const addressParts = [
        customer.address,
        customer.state_province ? `State/Province: ${customer.state_province}` : null,
        customer.division ? `Division: ${customer.division}` : null,
        customer.country,
        customer.legal_name && customer.legal_name !== customer.billing_name
          ? `Legal: ${customer.legal_name}`
          : null,
        primary
          ? `Attn: ${primary.full_name}${primary.title ? ` (${primary.title})` : ''}`
          : null,
      ].filter(Boolean);
      setDocForm((prev) => ({
        ...prev,
        client_name: customer.billing_name || '',
        client_email: customer.billing_email || primary?.email || '',
        client_address: addressParts.join('\n'),
        client_tax_id: customer.tax_id || '',
        client_tax_id_type: customer.tax_id_type || defaultTaxIdTypeForCountry(customer.country_code),
        place_of_supply: customer.place_of_supply || customer.state_province || '',
        tax_exempt: Boolean(customer.tax_exempt),
        gst_treatment: customer.gst_treatment || 'regular',
        tax_regime: defaultRegimeForCountry(customer.country_code),
      }));
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    }
  };

  const applyRequisition = async (requisitionId) => {
    if (!requisitionId) {
      setDocForm((prev) => ({ ...prev, requisition_id: '' }));
      return;
    }
    try {
      const req = await api.get(`/requisitions/${requisitionId}`).then((r) => r.data);
      const packages = Array.isArray(req.packages) ? req.packages : [];
      const lines = packages.length
        ? packages.map((pkg) => ({
            description: pkg.package_name || 'Service',
            quantity: pkg.quantity ?? 1,
            unit_price: pkg.unit_price ?? 0,
            sac_hsn: '',
          }))
        : req.estimated_value != null && req.estimated_value !== ''
          ? [
              {
                description: req.title || 'Estimated services',
                quantity: 1,
                unit_price: Number(req.estimated_value) || 0,
                sac_hsn: '',
              },
            ]
          : [emptyLine()];
      const terms = [req.payment_terms, req.special_terms, req.sla_notes].filter(Boolean).join('\n\n');
      const notes = [
        req.scope_summary,
        req.deliverables ? `Deliverables\n${req.deliverables}` : '',
        req.milestones ? `Milestones\n${req.milestones}` : '',
      ]
        .filter(Boolean)
        .join('\n\n');
      setDocForm((prev) => ({
        ...prev,
        requisition_id: String(req.id),
        currency: req.currency || prev.currency,
        lines,
        terms,
        notes,
        valid_until: req.end_date ? String(req.end_date).slice(0, 10) : prev.valid_until,
      }));
      if (req.customer_id) await applyCustomerToForm(req.customer_id);
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    }
  };

  const presetAppliedRef = useRef(false);
  const applyRequisitionRef = useRef(null);
  applyRequisitionRef.current = applyRequisition;
  useEffect(() => {
    if (!isNew || preferredType !== 'quotation' || !presetRequisitionId || !ready) return;
    if (presetAppliedRef.current) return;
    presetAppliedRef.current = true;
    void applyRequisitionRef.current?.(presetRequisitionId);
  }, [isNew, preferredType, presetRequisitionId, ready]);

  const saveMutation = useMutation(
    (payload) =>
      isNew
        ? api.post('/services/sales/documents', payload)
        : api.put(`/services/sales/documents/${documentId}`, payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(queryKeys.salesDocuments());
        const id = res.data?.id || res.data?.document?.id || documentId;
        if (isNew && id) {
          navigate(`/sales/documents/${id}`, { replace: true });
        } else {
          queryClient.invalidateQueries(['sales-document', documentId]);
          void appAlert('Document saved.');
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const sendEmailMutation = useMutation(
    () => api.post(`/services/sales/documents/${documentId}/send-email`),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(queryKeys.salesDocuments());
        queryClient.invalidateQueries(['sales-document', documentId]);
        const to = res.data?.to;
        void appAlert(to ? `Email sent to ${to}` : 'Email sent');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const sellerProfile = useMemo(() => {
    const stored = !isNew ? detail?.seller_snapshot : null;
    const storedHasIdentity =
      stored &&
      (stored.legal_name ||
        stored.trade_name ||
        stored.address_lines ||
        (stored.tax_ids || []).some((t) => t.value));
    if (storedHasIdentity) return stored;
    const profiles = orgTax?.profiles || [];
    const brand = (companies || []).find((c) => String(c.id) === String(docForm.company_id));
    const live = selectOrgTaxProfile(profiles, orgTax?.default_id, {
      countryCode: stored?.country_code || brand?.erp_country_code || '',
      currency: docForm.currency || brand?.erp_base_currency || '',
    });
    if (live && (live.legal_name || live.trade_name || (live.tax_ids || []).some((t) => t.value))) {
      return live;
    }
    return stored || null;
  }, [orgTax, companies, docForm.company_id, docForm.currency, isNew, detail]);

  const lineTotals = useMemo(() => {
    const subtotal = (docForm.lines || []).reduce(
      (sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
      0
    );
    const tax = docForm.tax_exempt ? 0 : subtotal * ((Number(docForm.tax_rate) || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [docForm.lines, docForm.tax_rate, docForm.tax_exempt]);

  const backToList = () => navigate('/erp/selling?tab=documents');

  const submitDoc = (e) => {
    e.preventDefault();
    if (readOnly) return;
    const payload = {
      ...docForm,
      company_id: docForm.company_id ? Number(docForm.company_id) : null,
      customer_id: docForm.customer_id ? Number(docForm.customer_id) : null,
      requisition_id:
        docForm.doc_type === 'quotation' && docForm.requisition_id
          ? Number(docForm.requisition_id)
          : null,
      template_id: docForm.template_id ? Number(docForm.template_id) : null,
      tax_rate: Number(docForm.tax_rate) || 0,
      lines: (docForm.lines || [])
        .filter((l) => String(l.description || '').trim())
        .map((l) => ({
          description: String(l.description).trim(),
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          sac_hsn: l.sac_hsn?.trim() || null,
        })),
    };
    if (!payload.lines.length) {
      void appAlert('Add at least one line item');
      return;
    }
    const brand = (companies || []).find((c) => String(c.id) === String(payload.company_id));
    if (!payload.company_id || !brand?.erp_company_id) {
      void appAlert(
        'This service brand is not linked to a legal company. Set the mapping under Accounting → Service brand → legal company, then save again.'
      );
      return;
    }
    saveMutation.mutate(payload);
  };

  if (!isNew && loadingDetail && !ready) {
    return <div className="loading">Loading document…</div>;
  }

  if (!isNew && (isError || (!loadingDetail && !detail))) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back to Sales" onClick={backToList} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">
            {error?.response?.data?.error || error?.message || 'Document not found'}
          </div>
        </div>
      </div>
    );
  }

  const title = isNew
    ? docForm.doc_type === 'invoice'
      ? 'New invoice'
      : 'New quotation'
    : readOnly
      ? `View ${detail?.number || 'document'}`
      : `Edit ${detail?.number || 'document'}`;

  return (
    <div className="svc-package-page list-page erp-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to Sales" onClick={backToList} />
        {!isNew && canManage && (
          <div style={{ marginLeft: 'auto' }}>
            <MoreMenu
              label="Document actions"
              items={[
                {
                  id: 'send-email',
                  label: sendEmailMutation.isLoading ? 'Sending email…' : 'Send email',
                  icon: FiMail,
                  disabled: sendEmailMutation.isLoading,
                  onSelect: async () => {
                    const to = String(docForm.client_email || '').trim();
                    const saved = String(detail?.client_email || '').trim();
                    if (!to) {
                      void appAlert('Add a client email on this form before sending.');
                      return;
                    }
                    if (to !== saved) {
                      void appAlert('Save this document first. Email goes to the client address on the form.');
                      return;
                    }
                    const ok = await appConfirm(
                      `Send ${detail?.number || 'this document'} to ${to}? The PDF and the email body from Settings will be included.`
                    );
                    if (ok) sendEmailMutation.mutate();
                  },
                },
              ]}
            />
          </div>
        )}
      </div>

      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <h1>{title}</h1>
          <p className="page-subtitle">
            {isNew
              ? 'Customer quotation or invoice.'
              : `${detail?.number || ''} · issued ${
                  detail?.issued_at ? formatDateOnly(detail.issued_at) : '—'
                }`}
          </p>
        </div>

        <form onSubmit={submitDoc} className="svc-package-form">
          <fieldset disabled={readOnly} className="svc-sales-fieldset">
            <div className="svc-form-grid svc-form-grid--3">
              <div className="form-group">
                <label htmlFor="sd-type">Type</label>
                <select
                  id="sd-type"
                  value={docForm.doc_type}
                  onChange={(e) => setDocForm({ ...docForm, doc_type: e.target.value })}
                  disabled={Boolean(!isNew) || readOnly}
                >
                  <option value="quotation">Quotation</option>
                  <option value="invoice">Invoice</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-status">Status</label>
                <select
                  id="sd-status"
                  value={docForm.status}
                  onChange={(e) => setDocForm({ ...docForm, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                  <option value="void">Void</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-template">Template</label>
                <select
                  id="sd-template"
                  value={docForm.template_id}
                  onChange={(e) => setDocForm({ ...docForm, template_id: e.target.value })}
                >
                  <option value="">Built-in layout</option>
                  {(templates || [])
                    .filter((t) => t.doc_type === 'both' || t.doc_type === docForm.doc_type)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {docForm.doc_type === 'quotation' && (
              <div className="form-group">
                <label htmlFor="sd-requisition">Requisition</label>
                <select
                  id="sd-requisition"
                  value={docForm.requisition_id || ''}
                  onChange={(e) => applyRequisition(e.target.value)}
                >
                  <option value="">Not linked</option>
                  {(requisitions || [])
                    .filter((row) => {
                      if (!docForm.customer_id) return true;
                      return (
                        String(row.customer_id) === String(docForm.customer_id) ||
                        String(row.id) === String(docForm.requisition_id)
                      );
                    })
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.title}
                        {row.customer_billing_name ? ` · ${row.customer_billing_name}` : ''}
                        {row.estimated_value != null
                          ? ` · ${money(row.estimated_value, row.currency)}`
                          : ''}
                      </option>
                    ))}
                </select>
                <p className="svc-terms-hint">
                  Linking a requisition fills the customer, estimate, line items, and terms.
                </p>
                {linkedRequisition ? (
                  <div className="svc-req-pop" role="region" aria-label="Linked requisition">
                    <div className="svc-req-pop-head">
                      <div>
                        <strong>{linkedRequisition.title}</strong>
                        <span className="svc-req-pop-status">{linkedRequisition.status}</span>
                      </div>
                      <Link to={`/requisitions?id=${linkedRequisition.id}`}>Open requisition</Link>
                    </div>
                    <p className="svc-req-pop-value">
                      Estimate{' '}
                      {linkedRequisition.estimated_value != null
                        ? money(linkedRequisition.estimated_value, linkedRequisition.currency)
                        : '—'}
                    </p>
                    <dl className="svc-req-pop-dl">
                      <div>
                        <dt>Type</dt>
                        <dd>{linkedRequisition.engagement_type || '—'}</dd>
                      </div>
                      <div>
                        <dt>Effective</dt>
                        <dd>{formatDateOnly(linkedRequisition.effective_date)}</dd>
                      </div>
                      <div>
                        <dt>End</dt>
                        <dd>{formatDateOnly(linkedRequisition.end_date)}</dd>
                      </div>
                      <div>
                        <dt>Term</dt>
                        <dd>
                          {linkedRequisition.term_months != null
                            ? `${linkedRequisition.term_months} months`
                            : '—'}
                        </dd>
                      </div>
                    </dl>
                    {(linkedRequisition.packages || []).length > 0 && (
                      <ul className="svc-req-pop-lines">
                        {linkedRequisition.packages.map((pkg) => (
                          <li key={pkg.id}>
                            <span>{pkg.package_name}</span>
                            <span>
                              {pkg.quantity ?? 1} × {money(pkg.unit_price, pkg.currency || linkedRequisition.currency)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {linkedRequisition.scope_summary ? (
                      <p>
                        <strong>Scope. </strong>
                        {linkedRequisition.scope_summary}
                      </p>
                    ) : null}
                    {linkedRequisition.deliverables ? (
                      <p>
                        <strong>Deliverables. </strong>
                        {linkedRequisition.deliverables}
                      </p>
                    ) : null}
                    {linkedRequisition.payment_terms ? (
                      <p>
                        <strong>Payment terms. </strong>
                        {linkedRequisition.payment_terms}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}

            <div className="svc-form-grid svc-form-grid--2">
              <div className="form-group">
                <label htmlFor="sd-customer">Customer</label>
                <select
                  id="sd-customer"
                  value={docForm.customer_id}
                  onChange={(e) => applyCustomerToForm(e.target.value)}
                >
                  <option value="">Manual client details…</option>
                  {(customers || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.billing_name}
                      {c.division ? ` · ${c.division}` : ''}
                      {c.country ? ` · ${c.country}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-pod">Service brand</label>
                <select
                  id="sd-pod"
                  value={docForm.company_id}
                  onChange={(e) => applyServiceBrand(e.target.value)}
                >
                  <option value="">None</option>
                  {(companies || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.erp_base_currency ? ` · ${c.erp_base_currency}` : ''}
                    </option>
                  ))}
                </select>
                {(() => {
                  const brand = (companies || []).find(
                    (c) => String(c.id) === String(docForm.company_id)
                  );
                  if (!docForm.company_id) {
                    return (
                      <p className="form-hint" style={{ marginTop: 6, fontSize: 12 }}>
                        Choose a service brand linked to a legal company.
                      </p>
                    );
                  }
                  if (!brand?.erp_company_id) {
                    return (
                      <p className="form-hint" style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
                        {brand?.name || 'This service brand'} is not linked to a legal company.
                        Set that under Accounting → Service brand → legal company before saving.
                      </p>
                    );
                  }
                  const mismatched =
                    String(docForm.currency || '').toUpperCase() !==
                    String(brand.erp_base_currency || '').toUpperCase();
                  return (
                    <p className="form-hint" style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                      Legal books: {brand.erp_company_name || 'mapped company'} (
                      {brand.erp_base_currency})
                      {mismatched
                        ? ' · Document currency differs; GL posts convert via Metadata FX rates when available.'
                        : ' · Document currency matches brand books.'}
                    </p>
                  );
                })()}
              </div>
            </div>

            <div className="svc-form-section-head">
              <h3>Seller tax profile</h3>
            </div>
            {sellerProfile ? (
              <div className="form-group">
                <p className="form-hint" style={{ marginTop: 0 }}>
                  {[sellerProfile.label, sellerProfile.legal_name || sellerProfile.trade_name]
                    .filter((part, index, all) => part && all.indexOf(part) === index)
                    .join(' · ')}
                  {sellerProfile.state_province ? ` · ${sellerProfile.state_province}` : ''}
                  {sellerProfile.country_code ? ` · ${sellerProfile.country_code}` : ''}
                </p>
                {sellerProfile.address_lines ? (
                  <p className="form-hint" style={{ marginTop: 2 }}>
                    {String(sellerProfile.address_lines)
                      .split('\n')
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                ) : null}
                {(sellerProfile.tax_ids || []).some((t) => t.value) ? (
                  <p className="form-hint" style={{ marginTop: 2 }}>
                    {(sellerProfile.tax_ids || [])
                      .filter((t) => t.value)
                      .map((t) => `${taxIdLabel(t.type)} ${t.value}`)
                      .join(' · ')}
                  </p>
                ) : null}
                <p className="form-hint" style={{ marginTop: 2 }}>
                  Printed on this {docForm.doc_type}. Edits in Settings → Org tax profile update
                  existing and new quotations and invoices.
                </p>
              </div>
            ) : (
              <p className="form-hint">
                No seller tax profile yet. Add one under Settings → Org tax profile so GST details
                print on quotations and invoices.
              </p>
            )}

            <div className="svc-form-grid svc-form-grid--2">
              <div className="form-group">
                <label htmlFor="sd-client-name">Client name (billing)</label>
                <input
                  id="sd-client-name"
                  value={docForm.client_name}
                  onChange={(e) => setDocForm({ ...docForm, client_name: e.target.value })}
                  required={!readOnly}
                />
              </div>
              <div className="form-group">
                <label htmlFor="sd-client-email">Client email</label>
                <input
                  id="sd-client-email"
                  type="email"
                  value={docForm.client_email}
                  onChange={(e) => setDocForm({ ...docForm, client_email: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="sd-address">Client address</label>
              <textarea
                id="sd-address"
                rows={2}
                value={docForm.client_address}
                onChange={(e) => setDocForm({ ...docForm, client_address: e.target.value })}
              />
            </div>

            <div className="svc-form-grid svc-form-grid--3">
              <div className="form-group">
                <label htmlFor="sd-tax-type">Client tax ID type</label>
                <select
                  id="sd-tax-type"
                  value={docForm.client_tax_id_type || ''}
                  onChange={(e) => setDocForm({ ...docForm, client_tax_id_type: e.target.value })}
                >
                  <option value="">—</option>
                  {TAX_ID_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-tax-id">Client tax ID</label>
                <input
                  id="sd-tax-id"
                  value={docForm.client_tax_id}
                  onChange={(e) => setDocForm({ ...docForm, client_tax_id: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="sd-pos">Place of supply</label>
                <input
                  id="sd-pos"
                  value={docForm.place_of_supply}
                  onChange={(e) =>
                    setDocForm({ ...docForm, place_of_supply: e.target.value.toUpperCase() })
                  }
                  placeholder="ON / CA / MH"
                />
              </div>
            </div>

            <div className="svc-form-grid svc-form-grid--4">
              <div className="form-group form-group--currency">
                <label htmlFor="sd-currency">Currency</label>
                <select
                  id="sd-currency"
                  value={docForm.currency || 'USD'}
                  onChange={(e) => setDocForm({ ...docForm, currency: e.target.value })}
                >
                  {(currencies || []).map((c) => {
                    const code = c.code || c.currency_code || c;
                    return (
                      <option key={code} value={code}>
                        {code}
                        {c.name ? ` — ${c.name}` : ''}
                      </option>
                    );
                  })}
                  {docForm.currency &&
                    !(currencies || []).some(
                      (c) => (c.code || c.currency_code) === docForm.currency
                    ) &&
                    !['USD', 'INR', 'CAD', 'EUR'].includes(docForm.currency) && (
                      <option value={docForm.currency}>{docForm.currency}</option>
                    )}
                  {(!currencies || currencies.length === 0) &&
                    ['USD', 'INR', 'CAD', 'EUR', 'GBP', 'AUD'].map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-regime">Tax regime</label>
                <select
                  id="sd-regime"
                  value={docForm.tax_regime || 'manual'}
                  onChange={(e) => setDocForm({ ...docForm, tax_regime: e.target.value })}
                >
                  {TAX_REGIME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-tax-rate">Tax % (US / IN rate)</label>
                <input
                  id="sd-tax-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={docForm.tax_rate}
                  onChange={(e) => setDocForm({ ...docForm, tax_rate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="sd-issued">Issued</label>
                <input
                  id="sd-issued"
                  type="date"
                  value={docForm.issued_at}
                  onChange={(e) => setDocForm({ ...docForm, issued_at: e.target.value })}
                />
              </div>
            </div>

            <div className="svc-form-grid svc-form-grid--2">
              <div className="form-group">
                <label htmlFor="sd-gst">GST treatment</label>
                <select
                  id="sd-gst"
                  value={docForm.gst_treatment || 'regular'}
                  onChange={(e) => setDocForm({ ...docForm, gst_treatment: e.target.value })}
                >
                  {GST_TREATMENT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sd-exempt">Tax exempt</label>
                <select
                  id="sd-exempt"
                  value={docForm.tax_exempt ? '1' : '0'}
                  onChange={(e) => setDocForm({ ...docForm, tax_exempt: e.target.value === '1' })}
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>
            </div>

            <div className="svc-form-grid svc-form-grid--2">
              {docForm.doc_type === 'quotation' ? (
                <div className="form-group">
                  <label htmlFor="sd-valid">Valid until</label>
                  <input
                    id="sd-valid"
                    type="date"
                    value={docForm.valid_until}
                    onChange={(e) => setDocForm({ ...docForm, valid_until: e.target.value })}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="sd-due">Due date</label>
                  <input
                    id="sd-due"
                    type="date"
                    value={docForm.due_at}
                    onChange={(e) => setDocForm({ ...docForm, due_at: e.target.value })}
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="sd-notes">Notes</label>
                <input
                  id="sd-notes"
                  value={docForm.notes}
                  onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="svc-form-section-head">
              <h3>Line items</h3>
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setDocForm({ ...docForm, lines: [...docForm.lines, emptyLine()] })}
                >
                  <FiPlus /> Add line
                </button>
              )}
            </div>
            <div className="svc-sales-lines">
              {docForm.lines.map((line, idx) => (
                <div key={idx} className="svc-sales-line-row">
                  <input
                    placeholder="Description / service"
                    aria-label={`Line ${idx + 1} description`}
                    value={line.description}
                    onChange={(e) => {
                      const lines = [...docForm.lines];
                      lines[idx] = { ...lines[idx], description: e.target.value };
                      setDocForm({ ...docForm, lines });
                    }}
                  />
                  <input
                    placeholder="SAC/HSN"
                    aria-label={`Line ${idx + 1} SAC/HSN`}
                    value={line.sac_hsn || ''}
                    onChange={(e) => {
                      const lines = [...docForm.lines];
                      lines[idx] = { ...lines[idx], sac_hsn: e.target.value };
                      setDocForm({ ...docForm, lines });
                    }}
                    style={{ maxWidth: 100 }}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Qty"
                    aria-label={`Line ${idx + 1} quantity`}
                    value={line.quantity}
                    onChange={(e) => {
                      const lines = [...docForm.lines];
                      lines[idx] = { ...lines[idx], quantity: e.target.value };
                      setDocForm({ ...docForm, lines });
                    }}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Unit price"
                    aria-label={`Line ${idx + 1} unit price`}
                    value={line.unit_price}
                    onChange={(e) => {
                      const lines = [...docForm.lines];
                      lines[idx] = { ...lines[idx], unit_price: e.target.value };
                      setDocForm({ ...docForm, lines });
                    }}
                  />
                  <span className="svc-sales-line-amount">
                    {money(
                      (Number(line.quantity) || 0) * (Number(line.unit_price) || 0),
                      docForm.currency
                    )}
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      aria-label={`Remove line ${idx + 1}`}
                      onClick={() =>
                        setDocForm({
                          ...docForm,
                          lines: docForm.lines.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="svc-sales-totals">
              <span>Subtotal {money(lineTotals.subtotal, docForm.currency)}</span>
              <span>Tax {money(lineTotals.tax, docForm.currency)}</span>
              <strong>Total {money(lineTotals.total, docForm.currency)}</strong>
            </div>

            <div className="form-group">
              <label htmlFor="sd-terms">Terms</label>
              <textarea
                id="sd-terms"
                rows={6}
                value={docForm.terms}
                onChange={(e) => setDocForm({ ...docForm, terms: e.target.value })}
                placeholder={'Payment terms, validity\n\n**Bold**, *italic*, headings, and lists are supported'}
                readOnly={readOnly}
              />
              <p className="svc-terms-hint">
                Markdown is saved with the quotation and printed the same way on the PDF.
              </p>
              {docForm.terms?.trim() ? (
                <div
                  className="svc-terms-preview"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(docForm.terms) }}
                />
              ) : null}
            </div>
          </fieldset>

          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={backToList}>
              {readOnly ? 'Back' : 'Cancel'}
            </button>
            {!readOnly && (
              <LoadingButton
                type="submit"
                className="btn btn-primary"
                loading={saveMutation.isLoading}
                icon={FiPlus}
              >
                {isNew ? 'Create' : 'Save'}
              </LoadingButton>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesDocumentDetails;
